import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { findLang } from '../../../common/models/lang';
import { CarImage } from '../CarImage';
import { currency } from '../../../common/utils/currency';
import { useNavigateWithSearch } from '../../../common/hooks/useNavigateWithSearch';
import { FREE_GATE_SERVICE_TYPES, SERVICE_TYPE_ID } from '../../models/service-type';
import { MAIN, TOP, mapItemIdToChips, BOTTOM } from './mapItemIdToChips';
import { uniqueId } from 'lodash';
import AlChip from '../../../common/components/AlChip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { TRANSFER } from '../../models/transfer';
import { getSpecialTicketChips } from './getSpecialTicketChips';
import { logAmplitudeEvent } from '../../../common/services/amplitudeService';
import { SERVICES } from '../../../constants';
import { AirportShuttlePlusUpsell } from './AirportShuttlePlusUpsell';
import { getServiceTypeKey } from '../../../common/utils/getServiceTypeKey';

const _isMeetAndGreetAvailable = (transfer) =>
    transfer.typeId === TRANSFER.FROM_MUNICH || transfer.typeId === TRANSFER.FROM_NUREMBERG;

const BookingChip = ({ meta }) => {
    const { t } = useTranslation();
    const label = t(meta.label);

    return <AlChip color={meta.color} backgroundColor={meta.backgroundColor} label={label} icon={meta.icon}></AlChip>;
};

const TopSection = ({ imageUrl, priceString, masterPriceString }) => {
    const { t } = useTranslation();

    return (
        <Box>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                <CarImage url={imageUrl} />
                {(priceString && (
                    <Stack sx={{ alignItems: 'flex-end' }}>
                        <Typography variant="h5" sx={{ mb: 0.5 }}>
                            {t('transfer-total')}
                        </Typography>
                        {masterPriceString && (
                            <Typography variant="h5" sx={{ fontWeight: 500 }}>
                                <del>{masterPriceString}</del>
                            </Typography>
                        )}
                        <Typography
                            variant="h4"
                            color={masterPriceString ? 'secondary' : 'inherit'}
                            sx={{ fontWeight: 500 }}
                        >
                            {priceString}
                        </Typography>
                    </Stack>
                )) || (
                    <Typography variant="body2" sx={{ textAlign: 'right' }}>
                        {t('booking.priceOnRequest')}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

const ServiceDescription = ({ serviceTypeKey }) => {
    const { t, i18n } = useTranslation();
    const key = `booking.serviceInfo.description.${serviceTypeKey}`;

    if (!i18n.exists(key)) {
        return null;
    }

    return (
        <Typography variant="body1" sx={{ mt: 1, mb: 0.5 }}>
            {t(key)}
        </Typography>
    );
};

const MainSection = ({ chips, specialTicketChips, serviceTypeId, transfer }) => {
    const [expanded, setExpanded] = useState(false);
    const { t, i18n } = useTranslation();
    const isFirstMount = useRef(true);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        logAmplitudeEvent('Category: More Information Toggled', { expanded, serviceTypeId });
    }, [expanded]);

    const serviceTypeKey = getServiceTypeKey(serviceTypeId);

    // Get all available keys from the translation object
    const getTranslationKeys = (type) => {
        if (!type) return [];
        // Get the translation resource for the current language
        const resources = i18n.getResourceBundle(i18n.language, 'translations');
        const serviceInfo = resources?.booking?.serviceInfo?.[type];

        // Return all keys from the service type's translation object
        return serviceInfo ? Object.keys(serviceInfo) : [];
    };

    // Get translations by calling t() with the full path for each key
    const infoTexts = getTranslationKeys(serviceTypeKey)
        .filter((key) => {
            if (key === 'meetAndGreet' && !_isMeetAndGreetAvailable(transfer)) {
                return false;
            }
            return true;
        })
        .map((key) => t(`booking.serviceInfo.${serviceTypeKey}.${key}`));

    const title = t(`booking.serviceInfo.title.${serviceTypeKey}`);
    const topChips = [...specialTicketChips, ...chips[TOP]];

    return (
        <Box>
            <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h5" sx={{ pb: 0.25 }}>
                    {title}
                </Typography>
                <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
                    {topChips.map((chip) => (
                        <BookingChip key={uniqueId()} meta={chip} />
                    ))}
                </Stack>
            </Stack>

            <Box sx={{ pt: 2 }}>
                {chips[MAIN].map((chip) => (
                    <span style={{ display: 'inline-block', marginRight: '6px', marginBottom: '8px' }} key={uniqueId()}>
                        <BookingChip meta={chip} />
                    </span>
                ))}
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Box
                    onClick={handleExpandClick}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'secondary.main',
                        textDecoration: 'underline',
                        userSelect: 'none',
                        py: 1,
                    }}
                >
                    <span style={{ display: 'inline-block', marginRight: '6px' }}>
                        {t('booking.serviceInfo.moreInformation')}
                    </span>
                    <ExpandMoreIcon
                        sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                        }}
                    />
                </Box>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <ServiceDescription serviceTypeKey={serviceTypeKey} />
                    <Box component="ul" sx={{ m: 0, pl: 3, pb: 2 }}>
                        {infoTexts.map((text, index) => (
                            <Typography key={index} component="li">
                                {text}
                            </Typography>
                        ))}
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
};

const BottomSection = ({ chips, onSelect, sx, serviceTypeId }) => {
    const { t } = useTranslation();

    return (
        <Stack
            sx={{
                flexDirection: { sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                ...sx,
            }}
        >
            <Stack
                sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'flex-start',
                    mb: { xs: 1.5, sm: 0 },
                    mr: { sm: 2 },
                }}
            >
                {chips[BOTTOM] && chips[BOTTOM].length > 0 && (
                    <>
                        <BookingChip meta={chips[BOTTOM][0]} />
                        <Typography variant="caption" sx={{ ml: 1, mt: 0.5 }}>
                            {serviceTypeId === SERVICE_TYPE_ID.UPON_REQUEST
                                ? t('booking.freeCancellationNoteA2B')
                                : t('booking.freeCancellationNoteAirportTransfers')}
                        </Typography>
                    </>
                )}
            </Stack>
            <Button
                variant="contained"
                size="large"
                color="secondary"
                onClick={onSelect}
                data-cy="transferCategory"
                sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
                {t('select')}
            </Button>
        </Stack>
    );
};

const BookingCategoryItem = ({ item, updateFirstTransfer, url, transfer, legroomService, totalPassengers }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigateWithSearch();

    const langCode = findLang(i18n.language).code;

    const price = currency(item.price);
    const masterPrice = item.price < item.masterPrice ? currency(item.masterPrice) : '';
    const serviceTypeId = item.serviceTypeId;

    const chips = mapItemIdToChips(serviceTypeId, _isMeetAndGreetAvailable(transfer));
    const specialTicketChips = getSpecialTicketChips(item.servicePackCodes);

    const onSelect = () => {
        updateFirstTransfer({ name: 'serviceTypeId', value: item.serviceTypeId });
        updateFirstTransfer({ name: 'serviceType', value: item });

        // Preserve an already-selected legroom extra on COLLECTIVE; clear it on every other tier.
        const keepLegroom =
            item.serviceTypeId === SERVICE_TYPE_ID.COLLECTIVE &&
            Number(transfer.extras?.[SERVICES.EXTRA_LEGROOM] || 0) > 0;
        if (!keepLegroom) {
            updateFirstTransfer({ name: `extras.${SERVICES.EXTRA_LEGROOM}`, value: 0 });
        }

        // GATE is included free on INDIVIDUAL/EXCLUSIVE; on every other tier it must be reset
        // so a previously auto-set value can't leak across a category switch (it costs 10 € on
        // COLLECTIVE and the customer would re-opt-in via the Services step if they want it).
        updateFirstTransfer({
            name: 'extras.GATE',
            value: FREE_GATE_SERVICE_TYPES.includes(item.serviceTypeId) ? 1 : 0,
        });

        navigate(`${url}/details`);
    };

    const onShuttlePlusSelect = (maxQty) => {
        updateFirstTransfer({ name: 'serviceTypeId', value: item.serviceTypeId });
        updateFirstTransfer({ name: 'serviceType', value: item });
        updateFirstTransfer({ name: `extras.${SERVICES.EXTRA_LEGROOM}`, value: maxQty });
        updateFirstTransfer({ name: 'extras.GATE', value: 0 });
        navigate(`${url}/details`);
    };

    const handleRenderMainSection = (serviceTypeId, transfer) => {
        if (serviceTypeId === SERVICE_TYPE_ID.UPON_REQUEST) {
            const infoText = item[`description${langCode}`].p;

            return (
                <Box sx={{ mr: { sm: 2 } }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        {t('booking.serviceInfo.title.uponRequest')}
                    </Typography>
                    <Typography sx={{ mb: 2 }}>{infoText}</Typography>
                </Box>
            );
        } else {
            return (
                <MainSection
                    chips={chips}
                    specialTicketChips={specialTicketChips}
                    serviceTypeId={serviceTypeId}
                    transfer={transfer}
                />
            );
        }
    };

    const hasUpsell = serviceTypeId === SERVICE_TYPE_ID.COLLECTIVE && legroomService;

    return (
        <Paper
            sx={{
                pt: 2,
                px: 2.5,
                pb: hasUpsell ? 0 : 2.5,
                overflow: 'hidden',
            }}
        >
            <TopSection imageUrl={item.descriptionImage} priceString={price} masterPriceString={masterPrice} />
            {handleRenderMainSection(serviceTypeId, transfer)}
            <BottomSection
                sx={{ mt: { xs: 0.25, sm: 1.25 } }}
                chips={chips}
                onSelect={onSelect}
                serviceTypeId={serviceTypeId}
            />
            {hasUpsell && (
                <AirportShuttlePlusUpsell
                    service={legroomService}
                    totalPassengers={totalPassengers}
                    selected={Number(transfer.extras?.[SERVICES.EXTRA_LEGROOM] || 0) > 0}
                    onSelect={onShuttlePlusSelect}
                    sx={{ mt: 2.5, mx: -2.5 }}
                />
            )}
        </Paper>
    );
};

export { BookingCategoryItem };
